/**
 * PA form three-dots menu wiring (options, handlers, nested modals).
 * Sibling of {@link MedicalPaThreeDotsMenu}, driven by the Postgres-backed
 * `generalOrderDetails` and `authOnFile` payloads.
 *
 * @module PaFormThreeDotsMenu
 */
import { Item } from "../../../data-model/documentModalWithSideBav";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { Submission } from "../../../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { UseHealthCareFacilityData } from "../../../hooks/useHealthCareFacility";
import ThreeDotsMenu from "../ThreeDotsMenu";
import ThreeDotsModals from "../ThreeDotsModals";
import { useMenuActions, useMenuOptions, usePmgIngestionExists } from "./hooks";

export interface PaFormThreeDotsMenuProps {
  /** NCCN guideline items for the order */
  items: Item[];
  /** Health care facility data */
  healthCareFacilityData: UseHealthCareFacilityData;
  /** Auth-on-file details (Postgres-backed) */
  authOnFile: AuthOnFile;
  /** General order details (Postgres-backed) */
  generalOrderDetails: GeneralOrderDetails;
  /** Submission data */
  submissionData: Submission;
  /** BigQuery screen name used for analytics events emitted from this menu. */
  screenName: ScreenNamesForBigQuery;
}

/**
 * A specialized three-dots menu for the PA form screen.
 *
 * Wraps the generic {@link ThreeDotsMenu} with PA form specific:
 * - Options (View Guidelines, View Tx Plan, View in Onco EMR, View DX Codes,
 *   View NAR Grid, and Ingest in Patient Graph while PMG data is absent)
 * - Action handlers (with event tracking) sourcing context from `generalOrderDetails` and `authOnFile`
 * - Modals (Doc Viewer, Image Viewer, NAR Grid)
 */
const PaFormThreeDotsMenu: React.FC<PaFormThreeDotsMenuProps> = ({
  items,
  healthCareFacilityData,
  authOnFile,
  generalOrderDetails,
  submissionData,
  screenName,
}) => {
  const { status: pmgIngestionStatus, refetch: refetchPmgIngestionExists } =
    usePmgIngestionExists(generalOrderDetails);
  const {
    executeAction,
    handleMouseEnter,
    abortControllerRef,
    openedModalId,
    isIngesting,
  } = useMenuActions({
    items,
    healthCareFacilityData,
    authOnFile,
    generalOrderDetails,
    submissionData,
    screenName,
    onIngestComplete: refetchPmgIngestionExists,
  });
  const { options } = useMenuOptions(
    items,
    pmgIngestionStatus?.exists ?? false,
    isIngesting,
  );

  return (
    <>
      <ThreeDotsMenu
        options={options}
        onOptionClick={executeAction}
        onMenuOpen={handleMouseEnter}
        id="pa-form-three-dots-menu"
      />
      <ThreeDotsModals
        openedModalId={openedModalId}
        authOnFile={authOnFile}
        abortControllerRef={abortControllerRef}
        screenName={screenName}
      />
    </>
  );
};

export default PaFormThreeDotsMenu;
